import { useCallback, useEffect, useState } from 'react';
import { EventAccordionItemProps } from 'src/components/Accordions';
import { ButtonWithIcon } from 'src/components/Buttons';
import * as sectionStyles from 'src/components/Listings/InventoryEventPage/Sections/InventoryEventFulfillmentSection.css';
import { SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY } from 'src/components/Purchases/PurchaseActionDropdown';
import { SellerAccountEmployeeSelector } from 'src/components/Selectors/SellerAccountEmployeeSelector';
import { useAppContext } from 'src/contexts/AppContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { Content } from 'src/contexts/ContentContext';
import { DialogId } from 'src/contexts/DialogContext/DialogContext';
import { useDialog } from 'src/contexts/DialogContext/useDialog';
import { PosSpinner } from 'src/core/POS/PosSpinner';
import { vars } from 'src/core/themes';
import { Button, Stack } from 'src/core/ui';
import { BulkUpdateSalesFulfillerDialog } from 'src/dialogs/BulkEdits/BulkUpdateSalesFulfillerDialog';
import { useSellerAccountEventFulfillmentRules } from 'src/hooks/api/useSellerAccountEventFulfillmentRules';
import { useBasicDialog } from 'src/hooks/useBasicDialog';
import { DeleteIcon } from 'src/svgs/Viagogo';
import { EMPTY_GUID } from 'src/utils/constants/constants';
import { ContentId } from 'src/utils/constants/contentId';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  BulkEditPreviewWithDetails,
  BulkEditSaleClient,
  EventWithData,
  Listing,
} from 'src/WebApiController';

import * as styles from '../InventoryEventPage.css';

type InventoryEventContentProps = {
  accordionItemProps?: EventWithData;
  inventoryAccordionItem?: EventAccordionItemProps<Listing>;
};

export function InventoryEventFulfillmentRulesSection({
  accordionItemProps,
  inventoryAccordionItem,
}: InventoryEventContentProps) {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();

  const updateKey = SALE_BULK_UPDATE_FULFILLER_UPDATE_KEY;
  const { BodyComponent } = inventoryAccordionItem || {};
  const event = accordionItemProps?.event;

  const [showSelector, setShowSelector] = useState(false);
  const [selectedFulfillerId, setSelectedFulfillerId] = useState(EMPTY_GUID);
  const [existingRuleId, setExistingRuleId] = useState(-1);
  const [existingSellerFulfillerId, setExistingSellerFulfillerId] = useState<
    string | undefined
  >(undefined);

  const {
    eventFulfillmentRuleQuery,
    handleSaveChanges,
    handleResetSetting,
    isMutating,
  } = useSellerAccountEventFulfillmentRules(
    event?.viagId,
    event?.mpId,
    existingRuleId > 0 ? existingRuleId : null
  );

  const onCreateNewSetting = () => {
    setShowSelector(true);
  };

  const updateDialog = useBasicDialog();

  const { openDialog, closeDialog } = useDialog(
    DialogId.BulkBackfillSalesFulfiller,
    BulkUpdateSalesFulfillerDialog
  );

  const onSaveChanges = (selectedFulfillerId: string) => {
    if (!selectedFulfillerId) return;

    openDialog({
      updateKey: updateKey,
      entityType: ActionOutboxEntityType.Sale,
      ...updateDialog.dialogProps,
      onSaveOverrideSettings: onSubmit,
      onBulkUpdate: onBulkUpdate,
      onCancel: () => {
        closeDialog();
      },
    });
  };

  const onSubmit = useCallback(() => {
    handleSaveChanges(selectedFulfillerId);
    setShowSelector(false);
  }, [handleSaveChanges, selectedFulfillerId]);

  const onBulkUpdate = useCallback(
    async (
      supportBackgroundProcess?: boolean,
      onPreviewReceived?: (preview: BulkEditPreviewWithDetails) => void,
      preview?: BulkEditPreviewWithDetails
    ) => {
      if (event?.viagId == null) {
        return;
      }

      if (onPreviewReceived) {
        await tryInvokeApi(
          async () => {
            const preview = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkBackfillSalesFulfillerPreview({
              eventId: event.viagId!,
              venueIds: null,
              fulfillerSellerUserId: selectedFulfillerId,
            });

            onPreviewReceived(preview);
          },
          (error) => {
            showErrorDialog('bulkBackfillSalesFulfillerPreview', error, {
              trackErrorData: { selectedFulfillerId },
            });
          }
        );
      } else {
        await tryInvokeApi(
          async () => {
            const succeeded = await new BulkEditSaleClient(
              activeAccountWebClientConfig
            ).bulkBackfillSalesFulfiller(
              {
                item1: preview!.preview,
                item2: selectedFulfillerId,
              },
              updateKey,
              supportBackgroundProcess
            );
            if (!supportBackgroundProcess) {
              closeDialog();
            }
          },
          (error) => {
            showErrorDialog('bulkBackfillSalesFulfiller', error, {
              trackErrorData: {
                preview,
              },
            });
          }
        );
      }
    },
    [
      activeAccountWebClientConfig,
      closeDialog,
      showErrorDialog,
      updateKey,
      event?.viagId,
      selectedFulfillerId,
    ]
  );

  useEffect(() => {
    const rule = eventFulfillmentRuleQuery.data;
    if (rule?.sellerAccountFulfillmentRuleId) {
      setExistingRuleId(rule.sellerAccountFulfillmentRuleId);
      setSelectedFulfillerId(rule.fulfillerSellerUserId);
      setExistingSellerFulfillerId(rule.fulfillerSellerUserId);
    } else {
      if (existingRuleId !== -1) {
        setExistingRuleId(-1);
      }
      if (selectedFulfillerId !== EMPTY_GUID) {
        setSelectedFulfillerId(EMPTY_GUID);
      }

      if (existingSellerFulfillerId !== undefined) {
        setExistingSellerFulfillerId(undefined);
      }

      if (showSelector) {
        setShowSelector(false);
      }
    }
  }, [
    event?.viagId,
    event?.mpId,
    eventFulfillmentRuleQuery.data,
    existingRuleId,
    selectedFulfillerId,
    existingSellerFulfillerId,
    showSelector,
  ]);

  const hasExistingRule = existingRuleId > 0;
  const canAddNew = !hasExistingRule && !showSelector;
  const hasSelectionChanged = selectedFulfillerId !== existingSellerFulfillerId;
  const isSaveButtonDisabled = hasExistingRule
    ? !hasSelectionChanged
    : isMutating || selectedFulfillerId === EMPTY_GUID;

  return (
    <div className={styles.tableSection}>
      {BodyComponent && accordionItemProps && (
        <div className={styles.tableContainer}>
          {isMutating ? (
            <PosSpinner />
          ) : (
            <Stack direction="column" gap="m">
              <span className={sectionStyles.sectionHeader}>
                <Content id={ContentId.FulfillmentOverrides} />
                <hr />
              </span>

              <Content id={ContentId.AllSalesDefaultFulfiller} />

              {canAddNew && (
                <>
                  <Stack direction="row" gap="m" width="full" alignItems="end">
                    <Button
                      disabled={isMutating}
                      onClick={onCreateNewSetting}
                      style={{ width: 'min-content', whiteSpace: 'nowrap' }}
                      variant={'regular'}
                    >
                      <Content id={ContentId.SetDefault} />
                    </Button>
                  </Stack>
                </>
              )}

              {(hasExistingRule || showSelector) && (
                <Stack direction="column" gap="m" width="full">
                  <SellerAccountEmployeeSelector
                    value={selectedFulfillerId ?? undefined}
                    disabled={isMutating}
                    allowUnchanged={false}
                    onChange={(newId) => {
                      setSelectedFulfillerId(newId);
                    }}
                    onClick={(e) => e.stopPropagation()}
                    isFulfillmentSection={true}
                  />

                  <Stack direction="row" gap="m" width="full" alignItems="end">
                    {hasExistingRule && (
                      <ButtonWithIcon
                        textContentId={ContentId.Delete}
                        variant={'outline'}
                        disabled={isMutating}
                        onClick={() => {
                          handleResetSetting();
                          setShowSelector(false);
                          setSelectedFulfillerId(EMPTY_GUID);
                          setExistingSellerFulfillerId(undefined);
                        }}
                        icon={
                          <DeleteIcon withHoverEffect size={vars.iconSize.m} />
                        }
                      />
                    )}
                    <Button
                      onClick={() => onSaveChanges(selectedFulfillerId)}
                      disabled={isSaveButtonDisabled}
                    >
                      <Content id={ContentId.Save} />
                    </Button>
                  </Stack>
                </Stack>
              )}
            </Stack>
          )}
        </div>
      )}
    </div>
  );
}
